import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import {
    Box,
    Typography,
    IconButton,
    Paper,
    Tooltip,
    Checkbox,
    FormControlLabel,
    Stack,
    TextField,
} from '@mui/material';
import { UploadFile, CheckCircle, Cancel, Description } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers';
import DmsDatePicker from "../DmsDatePicker/DmsDatePicker";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import { intl } from 'dmsbasereact';
import { getTranslatedDocumentName } from '../../utils/documentTranslations';

const ACCEPTED_FILE_TYPES = {
    'application/pdf': ['.pdf'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/png': ['.png'],
};

const ACCEPTED_EXTENSIONS = '.pdf, .jpg, .jpeg, .png';

/**
 * Document upload slot with expiration date field.
 * Extends the basic DocumentUploadSlot to support expiration dates.
 * Supports exemption declarations for documents that don't have an expiration date.
 */
export const DocumentUploadSlotWithExpiration = ({
    docName,
    file = null,
    expirationDate = null,
    existingFile = null,
    onFileSelect,
    requiresExpiration = false,
    disabled = false,
    error = false,
    errorMessage = '',
    allowRemove = true,
    isExempt = false,
    exemptionNote = '',
    onExemptionChange = null,
}) => {
    const [fileError, setFileError] = useState(null);

    const onDrop = useCallback(acceptedFiles => {
        setFileError(null);
        if (acceptedFiles.length > 0) {
            onFileSelect(docName, acceptedFiles[0], expirationDate);
        }
    }, [docName, onFileSelect, expirationDate]);

    const onDropRejected = useCallback((rejectedFiles) => {
        if (rejectedFiles.length > 0) {
            const rejection = rejectedFiles[0];
            if (rejection.errors?.some(e => e.code === 'file-invalid-type')) {
                setFileError(intl.get('InvalidFileType').d('Invalid file type. Accepted: ') + ACCEPTED_EXTENSIONS);
            } else {
                setFileError(intl.get('FileRejected').d('File rejected'));
            }
        }
    }, []);

    const handleExpirationChange = (newValue) => {
        const formattedDate = newValue ? newValue.format('YYYY-MM-DD') : null;
        onFileSelect(docName, file, formattedDate);
    };

    const handleExemptionToggle = (event) => {
        const newIsExempt = event.target.checked;
        if (onExemptionChange) {
            onExemptionChange(docName, newIsExempt, newIsExempt ? exemptionNote : '');
        }
    };

    const handleExemptionNoteChange = (event) => {
        if (onExemptionChange) {
            onExemptionChange(docName, isExempt, event.target.value);
        }
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        onFileSelect(docName, null, null);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        onDropRejected,
        multiple: false,
        disabled,
        accept: ACCEPTED_FILE_TYPES,
    });

    const hasFile = !!(file || existingFile);
    const displayName = file?.name || existingFile?.original_filename;

    const translatedDocName = getTranslatedDocumentName(docName);

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderStyle: 'dashed',
                borderColor: error ? 'error.main' : (isDragActive ? 'primary.main' : 'grey.400'),
                backgroundColor: isDragActive ? 'action.hover' : 'transparent',
            }}
        >
            {/* Document Title - always visible */}
            <Typography
                variant="subtitle2"
                sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: 'text.primary',
                    textAlign: 'center'
                }}
            >
                {translatedDocName}
            </Typography>

            <Box
                {...getRootProps()}
                sx={{
                    textAlign: 'center',
                    cursor: disabled ? 'default' : 'pointer',
                    minHeight: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column'
                }}
            >
                <input {...getInputProps()} />

                {hasFile ? (
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                        <Box display="flex" alignItems="center" overflow="hidden">
                            {existingFile && !file ? (
                                <Description color="primary" sx={{ mr: 1, flexShrink: 0 }} />
                            ) : (
                                <CheckCircle color="success" sx={{ mr: 1, flexShrink: 0 }} />
                            )}
                            <Typography variant="body2" noWrap title={displayName}>
                                {displayName}
                            </Typography>
                        </Box>
                        {!disabled && allowRemove && (
                            <Tooltip title={intl.get("Tooltip.Action.Remove").d("Remove")}>
                                <IconButton size="small" onClick={handleRemove} sx={{ flexShrink: 0 }}>
                                    <Cancel />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                ) : (
                    <Box display="flex" flexDirection="column" alignItems="center">
                        <UploadFile color="action" sx={{ mb: 1 }} />
                        <Typography variant="body2" color="textSecondary">
                            {intl.get('ClickOrDragToUpload').d('Click or drag file to upload')}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5 }}>
                            {intl.get('AcceptedFormats').d('Accepted formats')}: {ACCEPTED_EXTENSIONS}
                        </Typography>
                    </Box>
                )}
                {fileError && (
                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {fileError}
                    </Typography>
                )}
            </Box>

            {/* Expiration date field and exemption */}
            {(requiresExpiration || expirationDate) && (
                <Box sx={{ mt: 2 }}>
                    {/* Exemption checkbox with tooltip */}
                    {requiresExpiration && onExemptionChange && (
                        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isExempt}
                                        onChange={handleExemptionToggle}
                                        disabled={disabled}
                                        size="small"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                }
                                label={
                                    <Typography variant="body2">
                                        {intl.get("Document.Exemption.Label").d("Has exemption declaration")}
                                    </Typography>
                                }
                                sx={{ mr: 0 }}
                            />
                            <Tooltip
                                title={intl.get("Document.Exemption.Help").d(
                                    "Check this option if the institution has an official exemption declaration " +
                                    "issued by a public agency. Documents with exemption do not require an expiration date."
                                )}
                                arrow
                                placement="top"
                            >
                                <InfoOutlinedIcon
                                    fontSize="small"
                                    color="action"
                                    sx={{ cursor: 'help' }}
                                />
                            </Tooltip>
                        </Stack>
                    )}

                    {/* Exemption note field (shown when exempt is checked) */}
                    {isExempt && onExemptionChange && (
                        <TextField
                            label={intl.get("Document.Exemption.Note").d("Exemption Note (optional)")}
                            value={exemptionNote || ''}
                            onChange={handleExemptionNoteChange}
                            onClick={(e) => e.stopPropagation()}
                            fullWidth
                            size="small"
                            disabled={disabled}
                            multiline
                            rows={2}
                            placeholder={intl.get("Document.Exemption.NotePlaceholder").d("E.g.: Declaration issued by agency X on date Y")}
                            sx={{ mb: 1 }}
                        />
                    )}

                    {/* Expiration date picker (hidden when exempt) */}
                    {!isExempt && (
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                            <DmsDatePicker
                                label={intl.get("ExpirationDate").d("Expiration Date")}
                                value={expirationDate ? dayjs(expirationDate) : null}
                                onChange={handleExpirationChange}
                                format="DD/MM/YYYY"
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        size: "small",
                                        required: requiresExpiration && !isExempt,
                                        disabled: disabled,
                                        error: requiresExpiration && !isExempt && !expirationDate && hasFile,
                                        helperText: requiresExpiration && !isExempt && !expirationDate && hasFile ?
                                            intl.get("ExpirationDateRequired").d("Expiration date is required for this document") : '',
                                        onClick: (e) => e.stopPropagation(),
                                    }
                                }}
                                disabled={disabled}
                            />
                        </LocalizationProvider>
                    )}
                </Box>
            )}

            {error && !hasFile && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    {errorMessage || intl.get('RequiredField').d('This field is required')}
                </Typography>
            )}
        </Paper>
    );
};

DocumentUploadSlotWithExpiration.propTypes = {
    docName: PropTypes.string.isRequired,
    file: PropTypes.object,
    expirationDate: PropTypes.string,
    existingFile: PropTypes.shape({
        pk: PropTypes.number,
        original_filename: PropTypes.string,
    }),
    onFileSelect: PropTypes.func.isRequired,
    requiresExpiration: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    allowRemove: PropTypes.bool,
    isExempt: PropTypes.bool,
    exemptionNote: PropTypes.string,
    onExemptionChange: PropTypes.func,
};

